import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Minus } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { KINDS, RESOURCES } from "@/lib/constants";

const COLORS = ["#0ea5e9", "#6366f1", "#a855f7", "#f59e0b", "#10b981", "#64748b"];

const ROWS = [
  { group: "Evento" },
  { label: "Horario", get: (e) => `${e.start_time}–${e.end_time}`, text: true },
  { label: "Recorrido (m)", get: (e) => e.route_m },
  { label: "Puntos de corte", get: (e) => e.counts.cut_point },
  { label: "Tramos cortados", get: (e) => e.counts.street_cut },
  { label: "Zonas sin estacionamiento", get: (e) => e.counts.no_parking },
  { group: "Personal disponible" },
  ...RESOURCES.map((r) => ({ label: r.label, get: (e) => e.resources[r.key] ?? 0 })),
  { group: "Resultado del plan" },
  { label: "Efectivos usados", get: (e) => e.plan?.agents_used, lowerBetter: true },
  { label: "Unidades usadas", get: (e) => e.plan?.units_used, lowerBetter: true },
  { label: "Traslados", get: (e) => e.plan?.relocations },
  { label: "Relevos de turno", get: (e) => e.plan?.shift_handovers },
  { label: "Pico policial simultáneo", get: (e) => e.plan?.peak_police_need, lowerBetter: true },
  { label: "Agentes que faltan", get: (e) => e.plan?.missing_agents, lowerBetter: true },
  { label: "Desvíos calculados", get: (e) => e.detours_ok },
  { label: "Señales y material", get: (e) => e.signage_total, lowerBetter: true },
];

const Delta = ({ v, prev, lowerBetter }) => {
  if (v == null || prev == null || typeof v !== "number" || v === prev) return null;
  const d = v - prev;
  const good = lowerBetter ? d < 0 : null;
  const cls = good === null ? "text-sky-300" : good ? "text-emerald-400" : "text-red-400";
  return <span className={`font-mono text-[10px] ml-1.5 ${cls}`}>{d > 0 ? `+${d}` : d}</span>;
};

export default function CompareEditions() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/events/${id}/compare`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="p-10 font-mono text-xs text-slate-500">CARGANDO COMPARATIVA…</div>;
  const eds = data.editions.map((e, i, arr) => ({ ...e, key: arr.filter((x) => x.year === e.year).length > 1 ? e.date : e.year }));
  const chart = RESOURCES.map((r) => ({ name: r.label.replace("Agentes Policía Local (a pie)", "PL a pie").replace("Voluntarios ", "").replace("Personal de la ", "").replace("Auxiliares de Policía Local", "Auxiliares"), ...Object.fromEntries(eds.map((e) => [e.key, e.resources[r.key] ?? 0])) }));

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid="compare-page">
      <Link to={`/events/${id}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-300 mb-2" data-testid="compare-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Volver al evento</Link>
      <div className="font-mono text-xs tracking-[0.25em] text-sky-400 mb-2">COMPARATIVA HISTÓRICA</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Ediciones del evento</h1>
      <p className="text-slate-400 text-sm mt-2 mb-8">{eds.length} edición(es) encontradas (duplicadas entre sí o con el mismo nombre). Variación respecto a la edición anterior: <span className="text-emerald-400">mejora</span> / <span className="text-red-400">empeora</span>.</p>
      {eds.length < 2 && <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-6" data-testid="compare-single">Solo hay una edición. Use “Duplicar” para crear la del próximo año y poder comparar.</div>}

      <div className="bg-[#111c38] border border-white/10 rounded-xl overflow-x-auto mb-8" data-testid="compare-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3 font-mono text-[10px] tracking-widest text-slate-500 uppercase w-64">Indicador</th>
              {eds.map((e) => (
                <th key={e.id} className={`text-left px-5 py-3 ${e.is_current ? "bg-sky-500/10" : ""}`}>
                  <Link to={`/events/${e.id}`} className="font-display text-2xl font-bold hover:text-sky-300" data-testid={`compare-edition-${e.key}`}>{e.year}</Link>
                  <div className="font-mono text-[10px] text-slate-500">{e.date}{e.is_current ? " · ACTUAL" : ""}{!e.has_plan ? " · sin plan" : ""}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r, i) => r.group ? (
              <tr key={i}><td colSpan={eds.length + 1} className="px-5 pt-5 pb-2 font-mono text-[10px] tracking-widest text-amber-400 uppercase">{r.group}</td></tr>
            ) : (
              <tr key={i} className="border-t border-white/5">
                <td className="px-5 py-2 text-slate-300 text-xs">{r.label}</td>
                {eds.map((e, j) => {
                  const v = r.get(e);
                  return (
                    <td key={e.id} className={`px-5 py-2 ${e.is_current ? "bg-sky-500/5" : ""}`}>
                      <span className={r.text ? "font-mono text-xs" : "font-display font-semibold"}>{v ?? "—"}</span>
                      {j > 0 && !r.text && <Delta v={v} prev={r.get(eds[j - 1])} lowerBetter={r.lowerBetter} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#111c38] border border-white/10 rounded-xl p-5" data-testid="compare-staff-chart">
          <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-4">Personal disponible por edición</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} interval={0} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0b1329", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {eds.map((e, i) => <Bar key={e.id} dataKey={e.key} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#111c38] border border-white/10 rounded-xl p-5" data-testid="compare-elements-matrix">
          <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-4">Cortes, tramos y zonas por edición</div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr><th className="text-left pb-2 text-slate-500 font-normal">Elemento</th>{eds.map((e) => <th key={e.id} className="pb-2 font-mono text-slate-400">{e.year}</th>)}</tr></thead>
              <tbody>
                {data.elements.map((row, i) => {
                  const cur = eds.find((e) => e.is_current);
                  const inCur = cur && row.present[cur.id];
                  const isNew = inCur && eds.filter((e) => !e.is_current && e.date < cur.date).every((e) => !row.present[e.id]) && eds.some((e) => e.date < cur.date);
                  return (
                    <tr key={i} className="border-t border-white/5" data-testid={`compare-element-row-${i}`}>
                      <td className="py-1.5 pr-2">
                        <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: KINDS[row.kind]?.color }} />
                        <span className="text-slate-200">{row.name}</span>
                        {isNew && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">NUEVO</span>}
                        {cur && !inCur && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">NO ESTE AÑO</span>}
                      </td>
                      {eds.map((e) => <td key={e.id} className="text-center">{row.present[e.id] ? <Check className="w-3.5 h-3.5 text-emerald-400 inline" /> : <Minus className="w-3.5 h-3.5 text-slate-600 inline" />}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
